import { getSourceMaterialType, matchesMaterialTab, MaterialTabType, MaterialTabTypeFilterValues, normalizeMaterialTabType } from "@/types/MaterialTabs";
import { AppSettingType, Co2MaterialLibrary, Co2MaterialType } from "@api/apiClient";
import { Co2MaterialTypeQueryFilter, Co2MaterialTypeQueryResponse, useCo2MaterialTypesQuery } from "@api/co2MaterialTypes";
import { DensityType } from "@api/gql/graphql";
import DataGrid from "@components/DataGrid/DataGrid";
import { GridToolbarCompareButton } from "@components/DataGrid/components/toolbar/GridToolbarCompareButton";
import { useMaterialColumns } from "@components/MaterialSelectDialog/MaterialSelectGrid.columns";
import { transformMetadataToLocationOptions, transformMetadataToSubSystemOptions, transformMetadataToSystemOptions } from "@features/analysis/utils/metadataEntityUtils";
import useAppStore from "@hooks/useAppStore";
import useCompareMaterialsStore from "@hooks/useCompareMaterialsStore";
import { useMitt } from "@hooks/useMitt";
import i18n from "@i18n/i18n";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { alpha, Box, Checkbox, darken, lighten, Theme, Typography, useTheme } from "@mui/material";
import {
  getGridDefaultColumnTypes,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridFilterOperator,
  GridRowSelectionModel,
  GridSlotsComponentsProps,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import { observer } from "mobx-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import useState from "react-usestateref";

type ExtendedGridColDef = {
  hidden?: boolean;
  export?: boolean;
} & GridColDef;

const flattenSourceDataPaths = (value: any, prefix = "sourceData"): string[] => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const entries = Object.entries(value as Record<string, any>);
  const paths: string[] = [];
  entries.forEach(([key, child]) => {
    const path = `${prefix}.${key}`;
    paths.push(path);
    if (child && typeof child === "object" && !Array.isArray(child)) {
      paths.push(...flattenSourceDataPaths(child, path));
    }
  });
  return paths;
};

const getPathValue = (obj: any, path: string) => {
  return path.split(".").reduce((acc: any, key: string) => acc?.[key], obj);
};

const normalizeFieldName = (field: string) => field.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

export interface MaterialGridProps {
  height?: any;
  co2MaterialLibrary: Co2MaterialLibrary;
  searchText: string;
  metrics?: {
    length: { total: number };
    area: { total: number };
    volume: { total: number };
    quantity: { total: number };
  };
  compare?: boolean;
  compareSelectModel?: any;
  onRowSelectModelChange?: (co2MaterialLibrary: Co2MaterialLibrary, co2MaterialType: Co2MaterialType) => void;
  onCompareSelectModelChange?: (id: string, checked: boolean) => void;
  onShowCompare?: () => void;
  tabType: MaterialTabType;
  showTabs: boolean;
}

export const MaterialSelectGrid = observer(
  forwardRef((props: MaterialGridProps, ref: any) => {
    console.log("MaterialSelectGrid", props);
    const appStore = useAppStore();
    const compareMaterialsStore = useCompareMaterialsStore();
    const { getTabColumns, getDefaultColumns } = useMaterialColumns();
    const { emitter } = useMitt();
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>({});
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [paginationModel, setPaginationModel, paginationModelRef] = useState({ pageSize: 200, page: 0 });
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [rowCount, setRowCount] = useState(0);
    const [sortModel, setSortModel, sortModelRef] = useState<GridSortModel>([
      { field: "name", sort: "asc" },
      { field: "category", sort: "asc" },
      { field: "a1A2A3", sort: "asc" },
    ]);
    const [pageSizeOptions, setPageSizeOptions] = useState([10, 25, 50, 100]);
    const [sourceDataFields, setSourceDataFields] = useState<string[]>([]);
    const [hasRestoredState, setHasRestoredState] = useState(false);
    const apiRef = useGridApiRef();
    const localStorageKey = `matSelDlg-${props.co2MaterialLibrary.co2MaterialLibraryId}-${props.tabType}`;

    useImperativeHandle(ref, () => ({
      selectMaterial: (co2MaterialType: Co2MaterialType | null | undefined) => {
        const row = rows.find((r) => {
          if (co2MaterialType?.co2MaterialTypeId !== null) {
            return r.co2MaterialTypeId === co2MaterialType?.co2MaterialTypeId;
          }
          if (co2MaterialType?.externalId !== null) {
            return r.externalId === co2MaterialType?.externalId;
          }
          return false;
        });
        console.log(`selectMaterial: `, props.co2MaterialLibrary.name, row);
        if (row) {
          apiRef.current.selectRows([row.id], true, true);
        } else {
          apiRef.current.selectRows([], false, true);
        }
      },
    }));

    useEffect(() => {
      setSourceDataFields([]);
      setHasRestoredState(false);
    }, [props.co2MaterialLibrary.co2MaterialLibraryId, props.tabType]);

    useEffect(() => {
      if (hasRestoredState) return;
      try {
        const raw = localStorage.getItem(localStorageKey);
        if (!raw) {
          setHasRestoredState(true);
          return;
        }
        const saved = JSON.parse(raw);
        if (saved.paginationModel) setPaginationModel(saved.paginationModel);
        if (saved.sortModel) setSortModel(saved.sortModel);
        if (saved.filterModel) setFilterModel(saved.filterModel);
        if (saved.columnVisibilityModel) setColumnVisibilityModel(saved.columnVisibilityModel);
      } catch (error) {
        console.error("Failed to restore MaterialSelectGrid state", error);
      } finally {
        setHasRestoredState(true);
      }
    }, [localStorageKey, hasRestoredState]);

    useEffect(() => {
      if (!hasRestoredState) return;
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          paginationModel,
          sortModel,
          filterModel,
          columnVisibilityModel,
        }),
      );
    }, [localStorageKey, hasRestoredState, paginationModel, sortModel, filterModel, columnVisibilityModel]);

    useEffect(() => {
      const current = apiRef.current;
      const handleClose = async () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { filter, ...state } = current.exportState();
        const currentState = {
          ...state,
          sorting: { ...state.sorting, sortModel: sortModelRef.current },
          pagination: { ...state.pagination, paginationModel: paginationModelRef.current },
        };
        localStorage.setItem(localStorageKey, JSON.stringify(currentState));
      };

      emitter.on("close", handleClose); // TODO: create event type

      return () => {
        emitter.off("close", handleClose);
      };
    }, [apiRef]);

    useEffect(() => {
      const handleResetSelection = async (event: { co2MaterialLibrary: Co2MaterialLibrary; co2MaterialType: Co2MaterialType }) => {
        // Reset the selection for the other child grids
        if (event.co2MaterialLibrary.co2MaterialLibraryId !== props.co2MaterialLibrary.co2MaterialLibraryId) {
          console.log("Resetting selection for: ", props.co2MaterialLibrary.name);
          if (co2MaterialTypes === undefined) {
            setRowSelectionModel([]);
          }
        }
      };

      emitter.on("resetSelection", handleResetSelection); // TODO: create event type

      return () => {
        emitter.off("resetSelection", handleResetSelection);
      };
    }, []);

    // TODO: replace this with the new useAutosizeColumns hook
    useEffect(() => {
      const handleColumnVisibilityModelChange = (params: GridColumnVisibilityModel) => {
        const newColumns = apiRef.current.state.columns.orderedFields.reduce((acc: any, key: string) => {
          if (params[key] !== false) {
            acc.push(key);
          }
          return acc;
        }, []);
        console.log("newColumns", newColumns);

        setTimeout(() => {
          apiRef?.current?.autosizeColumns({
            columns: newColumns,
            includeOutliers: true,
            includeHeaders: true,
            disableColumnVirtualization: true,
            expand: true,
          });
        }, 1000);
      };

      return apiRef.current.subscribeEvent("columnVisibilityModelChange", handleColumnVisibilityModelChange);
    }, [apiRef]);
    // TODO: replace this with the new useAutosizeColumns hook

    useEffect(() => {
      setRowSelectionModel([]);
      setRows([]);
    }, [props.tabType, props.co2MaterialLibrary.co2MaterialLibraryId]);

    useEffect(() => {
      setPaginationModel({ pageSize: 200, page: 0 });
    }, [props.searchText, props.tabType]);

    const serverFilters = useMemo((): Co2MaterialTypeQueryFilter[] => {
      const gridFilters = (filterModel.items || [])
        .filter((item: any) => item?.field && (item?.value !== undefined || item?.operator === "isEmpty" || item?.operator === "isNotEmpty"))
        .map((item: any) => ({
          field: item.field,
          operator: item.operator || "contains",
          value: Array.isArray(item.value) ? undefined : String(item.value ?? ""),
          values: Array.isArray(item.value) ? item.value.map((x: any) => String(x)) : undefined,
        }));

      if (!props.showTabs) {
        return gridFilters;
      }

      return [{ field: "materialType", operator: "isAnyOf", values: MaterialTabTypeFilterValues[props.tabType] }, ...gridFilters];
    }, [filterModel.items, props.showTabs, props.tabType]);

    const co2MaterialTypes = useCo2MaterialTypesQuery(
      {
        materialLibraryIds: props.co2MaterialLibrary.co2MaterialLibraryId ? [props.co2MaterialLibrary.co2MaterialLibraryId] : [],
        showArchived: false,
        search: props.searchText,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortField: sortModel.length > 0 ? sortModel[0].field : "name",
        sortDirection: (sortModel[0]?.sort as "asc" | "desc" | undefined) ?? "asc",
        filters: serverFilters,
      },
      {
        enabled: Boolean(props.co2MaterialLibrary.co2MaterialLibraryId),
        placeholderData: props.showTabs ? undefined : (prevData: Co2MaterialTypeQueryResponse | undefined) => prevData,
      },
    );

    useEffect(() => {
      if (props.showTabs && co2MaterialTypes.isPlaceholderData) {
        return;
      }

      setRowCount(co2MaterialTypes.data?.totalCount || 0);
      const newRows = (co2MaterialTypes.data?.items || []).map((row: any) => {
        // Parse sourceData if it's a string, otherwise use as-is
        let source = row.sourceData ?? {};
        if (typeof source === 'string') {
          try {
            source = JSON.parse(source);
          } catch (error) {
            console.error("Error parsing sourceData:", error, source);
            source = {};
          }
        }
        
        return {
          ...row,
          id: row.co2MaterialTypeId || row.externalId,
          compare: props.compareSelectModel[row.co2MaterialTypeId || row.externalId] === true,
          type: normalizeMaterialTabType(getSourceMaterialType(source)),
          system: source.System ?? source.system ?? null,
          subsystem: source.Subsystem ?? source.subsystem ?? null,
          reference_period: source.ReferencePeriod ?? source.reference_period ?? null,
          attribute1: source.Attribute1 ?? source.attribute_1 ?? source.attribute1 ?? null,
          attribute2: source.Attribute2 ?? source.attribute_2 ?? source.attribute2 ?? null,
          attribute3: source.Attribute3 ?? source.attribute_3 ?? source.attribute3 ?? null,
          massPerUnit: row.unit,
          comment: source.Comment ?? source.comment ?? null,
          mapping_variable: source.MappingVariable ?? source.mapping_variable ?? null,
          reference_multiplier: source.ReferenceMultiplier ?? source.reference_multiplier ?? null,
          location: source.Location ?? source.location ?? null,
          // Keep sourceData as parsed object for column valueGetters
          sourceData: source,
        };
      });
      const filteredRows = props.showTabs
        ? newRows.filter((r) => matchesMaterialTab(r.sourceData, props.tabType))
        : newRows;

      setRows(filteredRows);
      const discoveredFields = newRows.flatMap((r: any) => flattenSourceDataPaths(r.sourceData));
      setSourceDataFields((prev) => {
        const merged = new Set([...prev, ...discoveredFields]);
        return Array.from(merged);
      });
      if ((co2MaterialTypes.data?.items?.length || 0) > 100) {
        setPageSizeOptions([10, 25, 50, 100, co2MaterialTypes.data?.items?.length || 0]);
      }
    }, [co2MaterialTypes.data, co2MaterialTypes.isPlaceholderData, props.tabType, props.showTabs, props.compareSelectModel]);

    const handleToggleCompareSelectModel = (params: any) => {
      const checked = params.row.compare === true;
      if (props.onCompareSelectModelChange) {
        props.onCompareSelectModelChange(params.row.id, params.row.compare);
        apiRef.current.updateRows([{ id: params.row.id, compare: !checked }]);
        compareMaterialsStore.toggleCo2MaterialType(params.row);
      }
    };

    const getMaterialTotalCo2 = (row: any, metrics: any) => {
      const co2Phases = ["a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "c1", "c2", "c3", "c4", "d", "biogenic"];

      // Safely sum all CO2 values, treating null/undefined as 0
      const totalCo2Impact = co2Phases.reduce((sum, key) => {
        const value = row[key];
        return sum + (typeof value === "number" ? value : 0);
      }, 0);

      switch ((row.unit || "").toLowerCase()) {
        case "m":
          return metrics.length.total * totalCo2Impact * 0.001;
        case "m2":
          return metrics.area.total * totalCo2Impact * 0.001;
        case "m3":
          return metrics.volume.total * totalCo2Impact * 0.001;
        case "kg":
          if (row.densityType === DensityType.Area) {
            return metrics.area.total * (row.density || 0) * totalCo2Impact * 0.001;
          }
          if (row.densityType === DensityType.Volume) {
            return metrics.volume.total * (row.density || 0) * totalCo2Impact * 0.001;
          }
          return 0;
        case "item":
        case "absolute":
          return metrics.quantity.total * totalCo2Impact * 0.001;
        default:
          return 0;
      }
    };

    useEffect(() => {
      let columnDefs: ExtendedGridColDef[];

      if (props.showTabs) {
        columnDefs = getTabColumns(props.tabType);
      } else {
        columnDefs = getDefaultColumns();
      }
      if (props.metrics !== undefined) {
        columnDefs.push({
          field: "co2eTotal",
          headerName: i18n.t("assign-material-dialog.columns.co2etotal"),
          flex: 1,
          minWidth: 200,
          type: "number",
          valueGetter: (_value: any, row: any) => {
            return getMaterialTotalCo2(row, props.metrics);
          },
          valueFormatter: (value: any) => {
            return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
          },
        });
      }

      const renderCompareCheckbox = (params: any) => {
        return <Checkbox icon={<CheckBoxOutlineBlankIcon />} checked={params.row.compare} onClick={() => handleToggleCompareSelectModel(params)} />;
      };

      if (props.compare) {
        columnDefs.push({
          field: "compare",
          headerName: i18n.t("assign-material-dialog.columns.compare"),
          flex: 1,
          minWidth: 100,
          headerAlign: "center",
          align: "center",
          renderCell: renderCompareCheckbox,
        });
      }

      // Add dynamic sourceData columns without duplicating existing mapped columns
      const existingNormalizedFields = new Set(
        columnDefs.map((c) => normalizeFieldName(c.field)).filter(Boolean),
      );
      const dynamicColumns: ExtendedGridColDef[] = sourceDataFields
        .filter((path) => {
          const leaf = path.split(".").pop() || path;
          return !existingNormalizedFields.has(normalizeFieldName(leaf));
        })
        .map((path) => {
          const leaf = path.split(".").pop() || path;
          return {
            field: path,
            headerName: leaf,
            flex: 1,
            minWidth: 120,
            type: "string",
            hideable: true,
            hidden: true,
            editable: false,
            valueGetter: (_value: any, row: any) => getPathValue(row, path),
          } as ExtendedGridColDef;
        });
      if (dynamicColumns.length > 0) {
        columnDefs = [...columnDefs, ...dynamicColumns];
      }

      configureColumns(appStore, columnDefs);
      setColumns(columnDefs);
      const sourceCol = columnDefs.find((c) => c.field === "source" || c.field === "sourceData") as any;
      if (sourceCol) {
        const updatedColumns = columnDefs.map((col) =>
          col.field === "source"
            ? {
                ...col,
                valueGetter: (value: any, row: any) => {
                  console.log("Getting value for source column", { value, row });
                  return value;
                },
              }
            : col,
        );
        setColumns(updatedColumns);
      }
       const visibilityModel = columnDefs.reduce((acc: any, col: any) => {
         if (col.hidden) acc[col.field] = false;
         return acc;
       }, {});
      // Initialize defaults for unknown fields, preserve user-customized state
      setColumnVisibilityModel((prev: any) => ({
        ...visibilityModel,
        ...prev,
      }));
    }, [props.tabType, props.showTabs, props.metrics, props.compare, sourceDataFields]);

    const handlePaginationModelChange = (paginationModel: any) => {
      console.log("handlePaginationModelChange", paginationModel);
      setPaginationModel(paginationModel);
      /*
    co2MaterialTypes.refetch({
      co2MaterialLibrary: props.co2MaterialLibrary.co2MaterialLibraryId,
      search: props.searchText,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    });
    */
    };

    return (
      <DataGrid
        apiRef={apiRef}
        loading={co2MaterialTypes.isFetching}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        rows={rows}
        hideFooter={false}
        pagination
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={(newPaginationModel) => handlePaginationModelChange(newPaginationModel)}
        rowCount={rowCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
          console.log("onRowSelectionModelChange", ids);
          setRowSelectionModel(ids);
          if (props.onRowSelectModelChange) {
            const selectedRow = rows.find((row: any) => row.co2MaterialTypeId === ids[0] || row.externalId === ids[0]);
            props.onRowSelectModelChange(props.co2MaterialLibrary, selectedRow);
          }
        }}
        onColumnVisibilityModelChange={(columnVisibilityModel: any) => {
          console.log("onColumnVisibilityModelChange", columnVisibilityModel);
          setColumnVisibilityModel(columnVisibilityModel);
        }}
        rowSelectionModel={rowSelectionModel}
        slots={{
          toolbar: CustomToolbarComponent,
        }}
        slotProps={{
          toolbar: {
            name: props.co2MaterialLibrary.name,
            showCompare: props.compare,
            onShowCompare: props.onShowCompare,
          },
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          setPaginationModel({ ...paginationModel, page: 0 });
          setSortModel(newSortModel);
        }}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => {
          setPaginationModel({ ...paginationModel, page: 0 });
          setFilterModel(newFilterModel);
        }}
        initialState={{
          density: "compact",
        }}
      />
    );
  }),
);

const locationWrapOperator = (operator: GridFilterOperator) => {
  const getApplyFilterFn: GridFilterOperator["getApplyFilterFn"] = (filterItem, column) => {
    const innerFilterFn = operator.getApplyFilterFn(filterItem, column);
    if (!innerFilterFn) {
      return innerFilterFn;
    }

    return (value, row, col, apiRef) => {
      const valueOption = (col as any).valueOptions.find((option: any) => option.alpha3 === filterItem.value);
      if (valueOption && valueOption.type === "country") {
        const filterItemRegion = { ...filterItem, value: valueOption.region };
        const innerFilterFnByRegion = operator.getApplyFilterFn(filterItemRegion, column)!;

        return innerFilterFn(value, row, col, apiRef) || innerFilterFnByRegion(value, row, col, apiRef);
      }
      if (valueOption && valueOption.type === "region") {
        const regionOption = (col as any).valueOptions.find((option: any) => option.alpha3 === filterItem.value);
        const filterItemRegion = { ...filterItem, value: regionOption.region };
        const innerFilterFnByRegion = operator.getApplyFilterFn(filterItemRegion, column)!;
        const selectedOption = (col as any).valueOptions.find((option: any) => option.alpha3 === value);

        return innerFilterFn(value, row, col, apiRef) || innerFilterFnByRegion(selectedOption.region, row, col, apiRef);
      }
      return innerFilterFn(value, row, col, apiRef);
    };
  };

  return { ...operator, getApplyFilterFn };
};

const configureColumns = (appStore: AppStore, columns: ExtendedGridColDef[]) => {
  const system = columns.find((c) => c.field === "system") as any;
  if (system) {
    system.type = "singleSelect";
    system.valueOptions = transformMetadataToSystemOptions(appStore.metadataEntities);
    system.valueOptions.shift(); // remove the default "-- Select System --" option
    system.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    system.getOptionLabel = (option: any) => {
      return option.label;
    };
    system.getOptionValue = (option: any) => {
      return option.label;
    };
  }
  const subSystem = columns.find((c) => c.field === "subsystem") as any;
  if (subSystem) {
    subSystem.type = "singleSelect";
    subSystem.valueOptions = transformMetadataToSubSystemOptions(appStore.metadataEntities);
    subSystem.valueOptions.shift(); // remove the default "-- Select SubSystem --" option
    subSystem.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    subSystem.getOptionLabel = (option: any) => {
      return option.label;
    };
    subSystem.getOptionValue = (option: any) => {
      return option.label;
    };
  }
  const location = columns.find((c) => c.field === "location" || c.field === "sourceData.Location") as any;
  if (location) {
    location.type = "singleSelect";
    location.valueOptions = transformMetadataToLocationOptions(appStore.metadataEntities);
    location.valueGetter = (value: any, _row: any, _colDef: any) => {
      return value;
    };
    location.getOptionLabel = (option: any) => {
      return option.label;
    };
    location.getOptionValue = (option: any) => {
      return option.alpha3;
    };
    const singleSelectColDef = getGridDefaultColumnTypes()["singleSelect"];
    location.filterOperators = singleSelectColDef.filterOperators?.map((operator: GridFilterOperator) => locationWrapOperator(operator));
  }
};

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    name: string;
    showCompare: boolean;
    onShowCompare?: () => void;
  }
}

const CustomToolbarComponent = (props: NonNullable<GridSlotsComponentsProps["toolbar"]>) => {
  const theme = useTheme();

  return (
    <GridToolbarContainer
      sx={{
        borderBottom: 1,
        backgroundColor: getBackgroundColor(theme),
        borderColor: getBorderColor(theme),
        fontWeight: "bold",
      }}
    >
      <Typography
        p={1}
        variant="body2"
        sx={{
          fontWeight: "bold",
        }}
      >
        {props.name}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{ disableToolbarButton: true }}
        printOptions={{ disableToolbarButton: true }}
        excelOptions={{
          allColumns: true,
        }}
      />
      {props.showCompare && (
        <GridToolbarCompareButton
          slotProps={{
            button: {
              onClick: () => {
                if (props.onShowCompare) {
                  props.onShowCompare();
                }
              },
            },
          }}
        />
      )}
    </GridToolbarContainer>
  );
};

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default MaterialSelectGrid;
